import React, { useCallback, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Maximize2, X } from 'lucide-react';
import { gallerySlides } from '../data/gallery';
const easeOut = [0.23, 1, 0.32, 1] as const;
export function ProductGallery() {
  const [[index, direction], setState] = useState<[number, number]>([0, 0]);
  const [isZoomed, setIsZoomed] = useState(false);
  const slide = gallerySlides[index];
  const paginate = useCallback((step: number) => {
    setState(([current]) => {
      const next = (current + step + gallerySlides.length) % gallerySlides.length;
      return [next, step];
    });
  }, []);
  const goTo = useCallback((next: number) => {
    setState(([current]) => [next, next > current ? 1 : -1]);
  }, []);
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowRight') paginate(1);
      if (event.key === 'ArrowLeft') paginate(-1);
      if (event.key === 'Escape') setIsZoomed(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [paginate]);
  return <section className="py-20 px-4 bg-white" data-id="element-250" data-mp="2zssblzvnhgae">
      <div className="max-w-5xl mx-auto" data-id="element-251" data-mp="3dqpcv9l1uave">
        <div className="text-center mb-10" data-id="element-252" data-mp="3dpnwcpl9kw6a">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-3 text-[#0B2545] uppercase" data-id="element-253" data-mp="38qulz619xlac">
            See Inside Nursing Instructor <span className="text-[#0A7D79]" data-id="element-254" data-mp="2l2l0y6bvx1k9">Atlas</span>
          </h2>
          <p className="text-xl font-semibold text-[#025B57] max-w-3xl mx-auto" data-id="element-255" data-mp="2h0zim5gkzv2o">
            Professional Nursing Training Material You Can Actually Teach From
          </p>
        </div>

        <div className="relative max-w-3xl mx-auto" data-id="element-256" data-mp="2q0cuf4s6gvqk">
          <div className="relative bg-[#F3F7FA] border border-slate-200 rounded-2xl shadow-sm shadow-[#0B2545]/10 overflow-hidden" role="group" aria-roledescription="carousel" aria-label="Product page previews" data-id="element-257" data-mp="2pzjxevj0tklm">
            <AnimatePresence initial={false} custom={direction} mode="wait" data-id="element-258" data-mp="1813onxshlu8">
              <motion.div key={slide.src} custom={direction} initial={{
              opacity: 0,
              x: direction > 0 ? 40 : -40
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: direction > 0 ? -40 : 40
            }} transition={{
              duration: 0.25,
              ease: easeOut
            }} drag="x" dragConstraints={{
              left: 0,
              right: 0
            }} dragElastic={0.15} onDragEnd={(_, info) => {
              if (info.offset.x < -60) paginate(1);else if (info.offset.x > 60) paginate(-1);
            }} className="p-4 sm:p-8 cursor-zoom-in touch-pan-y" onClick={() => setIsZoomed(true)} aria-roledescription="slide" aria-label={`${index + 1} of ${gallerySlides.length}`} data-id="element-259" data-mp="67m3f0ks9y80">
                <img src={slide.src} alt={slide.alt} draggable={false} className="w-full h-auto max-h-[70vh] object-contain mx-auto select-none rounded-lg" data-id="element-260" data-mp="2s75skae7yueq" />
              </motion.div>
            </AnimatePresence>

            <button type="button" onClick={() => setIsZoomed(true)} className="absolute top-4 right-4 w-10 h-10 rounded-full bg-[#0B2545]/90 text-white flex items-center justify-center hover:bg-[#0B2545] transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0A7D79]" aria-label="View image larger" data-id="element-261" data-mp="2eg0qz2rajs1r">
              <Maximize2 size={18} data-id="element-262" data-mp="1vhojdlg12q8a" />
            </button>
          </div>

          <button type="button" onClick={() => paginate(-1)} className="absolute left-0 sm:-left-5 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-[#0B2545] text-white border-2 border-[#B8C4CE] shadow-lg flex items-center justify-center hover:bg-[#061A33] transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0A7D79] focus-visible:ring-offset-2" aria-label="Previous image" data-id="element-263" data-mp="3utqxsh9souf3">
            <ChevronLeft size={24} data-id="element-264" data-mp="2iiewx6jzira1" />
          </button>
          <button type="button" onClick={() => paginate(1)} className="absolute right-0 sm:-right-5 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-[#0B2545] text-white border-2 border-[#B8C4CE] shadow-lg flex items-center justify-center hover:bg-[#061A33] transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0A7D79] focus-visible:ring-offset-2" aria-label="Next image" data-id="element-265" data-mp="2igvfvdqi8c77">
            <ChevronRight size={24} data-id="element-266" data-mp="1kvzeo2jqhplf" />
          </button>
        </div>

        <div className="max-w-3xl mx-auto text-center mt-6 min-h-[4.5rem]" aria-live="polite" data-id="element-267" data-mp="1puao4c649y34">
          <p className="text-base font-bold text-[#0B2545] uppercase tracking-wide" data-id="element-268" data-mp="167fveppy5qcj">
            Image 0{index + 1} — {slide.title}
          </p>
          <p className="text-sm text-slate-700/80 mt-1" data-id="element-269" data-mp="11aft8pw7x7qp">{slide.caption}</p>
        </div>

        <div className="flex justify-center items-center gap-3 mt-4" data-id="element-270" data-mp="1g6x3tewl2ic7">
          {gallerySlides.map((item, dotIndex) => <button key={item.src} type="button" onClick={() => goTo(dotIndex)} className={`h-2.5 rounded-full transition-[width,background-color] duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0A7D79] ${dotIndex === index ? 'w-8 bg-[#0A7D79]' : 'w-2.5 bg-[#0B2545]/25 hover:bg-[#0B2545]/40'}`} aria-label={`Go to image ${dotIndex + 1}`} aria-current={dotIndex === index} data-id="element-271" data-mp="1b95b4io46s6y" data-mpi={dotIndex} />)}
        </div>

        <div className="text-center mt-10 max-w-3xl mx-auto" data-id="element-272" data-mp="23x10t3f9nju8">
          <p className="text-lg font-bold text-[#0B2545]" data-id="element-273" data-mp="2azg2fnhhm6wd">
            <span className="text-[#025B57]" data-id="element-274" data-mp="2awtjkrp5c4yu">VISUAL TRAINING PAGES. REAL CLINICAL SKILLS.</span> ONE
            COMPLETE VISUAL SYSTEM.
          </p>
          <p className="text-lg text-slate-700/80 mt-3" data-id="element-275" data-mp="2kwu58kahmx67">
            Inside you&apos;ll find illustrations, labeled diagrams, clinical sequences, numbered breakdowns, ❌/✅
            comparisons, teaching cues, drills and quick checks.
          </p>
          <p className="text-lg font-semibold text-[#0B2545] mt-3" data-id="element-276" data-mp="2tribodrsukwl">
            Organized to help you run clearer, more structured and more engaging nursing sessions.
          </p>
        </div>
      </div>

      <AnimatePresence data-id="element-277" data-mp="33lr12slj9xqj">
        {isZoomed && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: easeOut
      }} className="fixed inset-0 z-50 bg-[#0B2545]/90 flex items-center justify-center p-4 overflow-auto" role="dialog" aria-modal="true" aria-label={slide.alt} onClick={() => setIsZoomed(false)} data-id="element-278" data-mp="38ln5gsy57r6m">
            <button type="button" onClick={() => setIsZoomed(false)} className="fixed top-4 right-4 w-11 h-11 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20 transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white" aria-label="Close enlarged image" data-id="element-279" data-mp="3mqdoysbxzmm4">
              <X size={22} data-id="element-280" data-mp="5bqj986m0n1j" />
            </button>
            <motion.img initial={{
          opacity: 0,
          scale: 0.97
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.97
        }} transition={{
          duration: 0.2,
          ease: easeOut
        }} src={slide.src} alt={slide.alt} className="max-w-full md:max-w-3xl h-auto rounded-xl shadow-2xl" onClick={event => event.stopPropagation()} data-id="element-281" data-mp="5affv9h2thay" />
          </motion.div>}
      </AnimatePresence>
    </section>;
}